import type { IGame } from './interfaces/IGame';
import { GAME_REGISTRY } from './GameRegistry';
import { NICHE_REGISTRY } from './NicheRegistry';
import { GenericArcadeEngine } from '../games/common/GenericArcadeEngine';

export class GameLoader {
  private currentGame: IGame | null = null;
  private isPausedState = false;

  async load(gameId: string, canvas: HTMLCanvasElement): Promise<IGame> {
    await this.unload();

    const meta = GAME_REGISTRY.find((g) => g.id === gameId) || NICHE_REGISTRY.find((g) => g.id === gameId);
    let gameInstance: IGame;

    if (meta && meta.loader) {
      try {
        const { default: GameClass } = await meta.loader();
        gameInstance = new GameClass();
      } catch (err) {
        console.error('Game load failed, falling back to GenericArcadeEngine:', err);
        // Dynamic fallback to GenericArcadeEngine
        gameInstance = new GenericArcadeEngine(gameId, meta ? meta.title : gameId);
      }
    } else {
      gameInstance = new GenericArcadeEngine(gameId, meta ? meta.title : gameId);
    }

    gameInstance.onScoreSubmit = (score, sessionMs) => {
      import('../core/events/EventBus').then(({ EventBus }) => {
        EventBus.emit('game:scoreSubmit', { gameId, score, sessionMs: sessionMs || 0 });
      });
    };

    gameInstance.onLiveScoreUpdate = (score) => {
      import('../core/events/EventBus').then(({ EventBus }) => {
        EventBus.emit('game:liveScoreUpdate', { gameId, score });
      });
    };

    await gameInstance.init(canvas);
    gameInstance.start();

    this.currentGame = gameInstance;
    this.isPausedState = false;
    return gameInstance;
  }

  pause(): void {
    if (this.currentGame) {
      this.currentGame.pause();
      this.isPausedState = true;
    }
  }

  resume(): void {
    if (this.currentGame) {
      this.currentGame.resume();
      this.isPausedState = false;
    }
  }

  async unload(): Promise<void> {
    if (this.currentGame) {
      this.currentGame.destroy();
      this.currentGame = null;
      this.isPausedState = false;
    }
  }

  get isPaused(): boolean {
    return this.isPausedState;
  }
}

export const gameLoader = new GameLoader();
